import { useQuery } from '@tanstack/react-query';
import { Loader2, Github, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SettingsLayout } from './SettingsLayout';
import { getGithubStatus } from '@/api/settings';

export function GitHubSettingsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['github-status'],
    queryFn: getGithubStatus,
  });

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const installed = data?.installed;
  const repos = data?.repositories || [];
  const installUrl = data?.install_url;
  const error = data?.error;

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            GitHub Integration
          </CardTitle>
          <CardDescription>
            Connect your GitHub account to create and manage projects.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {installed ? (
            <>
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 ml-2">
                  Your GitHub App is connected! You can create projects from your repositories.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  With GitHub connected, you can:
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Create new projects from your repositories</li>
                  <li>Automatically deploy when you push to branches</li>
                  <li>Manage deployment environments</li>
                </ul>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Repositories available via the installation</p>
                  {repos.length === 0 ? (
                    <Alert>
                      <AlertDescription>No repositories returned. Ensure the app has access to your repos.</AlertDescription>
                    </Alert>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border rounded">
                      <ul className="divide-y">
                        {repos.map((repo: any) => (
                          <li key={repo.id} className="p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{repo.full_name}</p>
                              <p className="text-xs text-muted-foreground">{repo.description}</p>
                            </div>
                            <Button variant="outline" size="sm" asChild>
                              <a href={repo.html_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View
                              </a>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" asChild>
                    <a href="https://github.com/settings/installations" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage GitHub App
                    </a>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Connect your GitHub account to get started with doploy. This allows us to:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Access your repositories to create projects</li>
                <li>Set up webhooks for automatic deployments</li>
                <li>Manage branches and deployment configurations</li>
              </ul>
              
              <Button asChild>
                <a href={installUrl || '/api/v1/github_app/install'}>
                  <Github className="h-4 w-4 mr-2" />
                  Connect GitHub Account
                </a>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
